import { Injectable, Injector, inject } from '@angular/core';
import { GeomStoreService, DB_CONNECTION_CONFIGS, OfflineModeService, PROJECT_PROVIDER } from '@luzula/luzula-lib';
import { DatabaseConfigService } from './database-config.service';
import { ProjectService } from './project.service';

/**
 * bpweb-Konfiguration des Geom-Stores (dedizierte DB `bpweb-geom`, ein Doc je
 * Fläche mit stabiler geomId — Standort/Humusform). Lifecycle/CRUD/Sync kommen
 * aus {@link GeomStoreService}; hier nur Remote-URL (inkl. Credentials) und
 * Name der lokalen Replik. (Muster: BereicheService.)
 */
@Injectable({ providedIn: 'root' })
export class GeomFeaturesService extends GeomStoreService {
	private dbConfig = inject(DatabaseConfigService);
	private dbConnectionConfigs = inject(DB_CONNECTION_CONFIGS);
	private injector = inject(Injector);
	private ps = inject(ProjectService);

	/**
	 * Gruppen des Projekts (feature/user-groups): neue/geaenderte Geom-Docs erben additiv die
	 * groups des Projekts. Lazy ueber PROJECT_PROVIDER (Kreis ProjectService->GEOM_STORE->dieser
	 * Service). Nur wenn das aktuelle Projekt-Doc zur projectId passt (sonst keine Stempelung).
	 */
	protected override groupsForProject(projectId: string): string[] | undefined {
		const ps = this.injector.get(PROJECT_PROVIDER, null) as { currentProjectDoc?: () => { _id?: string; groups?: string[] } | null } | null;
		const doc = ps?.currentProjectDoc?.();
		return doc && doc._id === projectId ? doc.groups : undefined;
	}

	/**
	 * Zwei Fassungen einer Editor-Fläche darf nur klären, wer die Flächen auch
	 * sonst bearbeiten dürfte — heute also der Entwickler.
	 *
	 * Gefragt wird `ProjectService.canEditGeomFeatures()`, dieselbe Stelle wie
	 * der Menüeintrag „Flächen bearbeiten". Wird das Recht erweitert, gilt es
	 * hier von selbst mit (Auflage des Nutzers 11.09.2026).
	 *
	 * Vorher schrieb die Klärung an jeder Prüfung vorbei, und das stille
	 * Aufräumen tat es sogar ohne Rückfrage.
	 */
	override darfAendern(_doc: any): boolean {
		return this.ps.canEditGeomFeatures();
	}

	/**
	 * Merkt am Projekt, dass gerade jemand an den Flächen gearbeitet hat.
	 *
	 * Treibt die Spalte „Letzte Änderung" in der Projektauswahl; das Drosseln
	 * und das Schreiben stehen im Projekt-Dienst der Bibliothek.
	 */
	protected override merkeArbeit(projectId: string): void {
		this.ps.merkeArbeitImProjekt(projectId);
	}

	/**
	 * Offline gestellt? Dann schiebt der Speicher nichts von selbst zum Server;
	 * alles wartet auf den Abgleich (dieselbe Lücke wie bei den Zeichnungen,
	 * Nutzer-Befund 25.09.2026). Erst im Moment der Frage geholt, nicht beim Bauen.
	 */
	protected override imOfflineModus(): boolean {
		return this.injector.get(OfflineModeService).userOfflineMode();
	}

	/** Remote-Master = `bpweb-geom` (nur online). */
	protected get remoteDbUrl(): string | null {
		if (!navigator.onLine) return null;
		const conn = this.dbConnectionConfigs[0];
		return conn ? conn.url(this.dbConfig.geomDbName) : null;
	}

	/** Lokale Replik (konstanter Name). */
	protected get localCacheDbName(): string {
		return `${this.dbConfig.geomDbName}-cache`;
	}

	/** Gateway-Auth: Bearer-Header pro Request für die Remote-Replikation. */
	protected override authHeaders(): Record<string, string> | null | undefined {
		return this.dbConnectionConfigs[0]?.getAuthHeaders?.() ?? null;
	}

	/**
	 * Abgelaufener Token: einmal still erneuern, dann wiederholt der fetch-Wrapper
	 * die Anfrage. Ohne das blieb die Replikation nach Token-Ablauf bei 401 stehen,
	 * bis die Seite neu geladen wurde.
	 */
	protected override authRefresh(): Promise<boolean> {
		return this.dbConnectionConfigs[0]?.refreshAuth?.() ?? Promise.resolve(false);
	}

	/**
	 * Auf dem Nur-Lese-Weg festgefahren: zurueck auf die richtige Adresse.
	 *
	 * Die Verbindung waehlt ihren Weg nur beim Aufbau. Startet die App ohne
	 * greifbaren Token (Standby, wackliges Netz), bleibt sie auf
	 * `/db/public/...` stehen — Ansehen geht, Speichern bekommt ein 405, und
	 * das fuer die ganze Sitzung (Nutzer-Befund 01.09.2026). Hier liegen die
	 * Flaechen des Karten-Editors.
	 */
	protected override adressReparatur(url: string): string | undefined {
		return this.dbConnectionConfigs[0]?.repariereAdresse?.(url);
	}
}
